import type { CSSProperties, ReactNode } from "react";
import { Icon } from "../../kit";

/**
 * The preset grid of Basic mode's rail panels, in iJewel's shape: three
 * columns of round previews with the name underneath, the chosen one ringed in
 * the one gold accent, an "add your own" tile first, and an × on the presets
 * the workspace owns.
 *
 * Shared by the material panels (through MaterialCategoryGrid) and the lighting
 * panels, so the five-plus grids on the rail cannot drift apart.
 */

const RING_ON = "0 0 0 2px #fff, 0 0 0 4px #946f2e";
const RING_OFF = "inset 0 0 0 1px rgba(0,0,0,0.12)";

export function PresetGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-3 gap-x-1.5 gap-y-1">{children}</div>;
}

/**
 * One preset. `preview` is the sphere's own style (a thumbnail URL as
 * `backgroundImage`, or a CSS sphere) — exactly one background property, never
 * the `background` shorthand next to a longhand (see MaterialCategoryGrid's
 * `swatchStyle` for why).
 */
export function PresetTile({
  label,
  preview,
  on,
  busy,
  disabled,
  onClick,
  onRemove,
  title,
  ...data
}: {
  label: string;
  preview: CSSProperties;
  on?: boolean;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  /** Shown as an × on hover/focus — only for presets the workspace owns. */
  onRemove?: () => void;
  title?: string;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <div className="group relative min-w-0">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={!!on}
        aria-busy={busy || undefined}
        title={title ?? label}
        className={`flex w-full min-w-0 flex-col items-center gap-1.5 rounded-[10px] px-0.5 pb-2 pt-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          on ? "bg-ed-active" : "hover:bg-ed-hover"
        }`}
        {...data}
      >
        <span
          className="relative grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full bg-ed-panel-2 bg-cover bg-center"
          style={{ ...preview, boxShadow: on ? RING_ON : RING_OFF }}
        >
          {busy ? (
            <span className="absolute inset-0 grid place-items-center bg-white/55">
              <Icon name="progress_activity" size={18} className="animate-spin text-ed-text-2" />
            </span>
          ) : null}
        </span>
        <span className="line-clamp-2 w-full break-words text-center text-[12px] leading-[15px] text-ed-text">{label}</span>
      </button>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          title={`Remove ${label}`}
          aria-label={`Remove ${label}`}
          className="absolute right-1 top-1 grid h-[22px] w-[22px] place-items-center rounded-full border border-ed-border bg-ed-panel text-ed-text-2 opacity-0 shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-opacity hover:text-ed-text focus-visible:opacity-100 disabled:hidden group-hover:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <Icon name="close" size={14} />
        </button>
      ) : null}
    </div>
  );
}

/** The "+" tile: add your own preset. Sits first, as in the reference. */
export function AddTile({
  label = "Add yours",
  title,
  onClick,
  busy,
  disabled,
  ...data
}: {
  label?: string;
  title?: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
  [k: `data-${string}`]: string | boolean | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy || undefined}
      title={title ?? label}
      className="flex min-w-0 flex-col items-center gap-1.5 rounded-[10px] px-0.5 pb-2 pt-2 text-ed-text-2 transition-colors hover:bg-ed-hover hover:text-ed-text disabled:cursor-not-allowed disabled:opacity-50"
      {...data}
    >
      <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full border border-dashed border-ed-line">
        <Icon name={busy ? "progress_activity" : "add"} size={22} className={busy ? "animate-spin" : ""} />
      </span>
      <span className="w-full text-center text-[12px] leading-[15px]">{label}</span>
    </button>
  );
}
